/* eslint-disable @typescript-eslint/no-explicit-any */
// src/app/pages/b2c-claim/b2c-claim.component.ts
import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { B2cClaimService } from '@app/services/b2c-claim.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

// import canonical country/state lists
import { countries, stateList } from '@app/constant/codes';

// validators
import { phone, noTrailingSpaceValidator, noSpacesValidator } from '@app/dto/common.dto';
import { Router } from '@angular/router';

type StepId = 1 | 2 | 3;

@Component({
  selector: 'app-b2c-claim',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './b2c-claim.component.html',
  styleUrls: ['./b2c-claim.component.css'],
})
export class B2cClaimComponent implements OnInit {
  currentStep: StepId = 1;
  skipSearch = false;
  submitted = false;
  errorMsg = '';
  advancedOpen = false;
  lastVerifiedTin: string | null = null;

  steps: Array<{ id: StepId; title: string; subtitle: string }> = [];
  captions: Record<number, string> = {};
  stickyCaptions: Record<number, string> = {};
  progress: Record<number, string> = {};
  filteredStateData: Array<{ State: string; Code?: string; CountryCode?: string }> = [];
  submissionUID: string = '';
  generatedID: string = '';
  logoUrl: string = '';

  onFooterLogoError: boolean = false;
  invoiceForm = new FormGroup({
    supplierTin: new FormControl('', [
      Validators.required,
      Validators.maxLength(14),
      noSpacesValidator(),
      noSpacesValidator(),
    ]),
    docNumber: new FormControl('', [
      Validators.required,
      noTrailingSpaceValidator(),
      noSpacesValidator(),
    ]),
    docDate: new FormControl('', Validators.required),
    totalValue: new FormControl('', [
      Validators.required,
      Validators.pattern(/^\d+(\.\d{1,2})?$/), // 🔒 only numbers
    ]),
  });

  buyerForm = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.maxLength(300),
      noSpacesValidator(),
    ]),
    idType: new FormControl('IC'),
    idNumber: new FormControl('', [Validators.required, noSpacesValidator()]),
    contact: new FormControl('', [Validators.required, phone, noSpacesValidator()]),
    email: new FormControl('', [
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      Validators.maxLength(320),
      noSpacesValidator(),
    ]),
    addr1: new FormControl('', [Validators.required, noSpacesValidator()]),
    addr2: new FormControl('', [noSpacesValidator()]),
    addr3: new FormControl('', [noSpacesValidator()]),
    city: new FormControl('', [Validators.required, noSpacesValidator()]),
    state: new FormControl('', Validators.required),
    country: new FormControl('', Validators.required),
    postal: new FormControl('', [noSpacesValidator()]),
    buyerTin: new FormControl('', [
      Validators.maxLength(14),
      noSpacesValidator(),
      noSpacesValidator(),
    ]),
    buyerSst: new FormControl('NA'),
  });

  review: any = {};
  brandMeta: any = null;
  b2cInvoiceId: string | null = null;

  lookupInProgress = false;
  lookupSuccess = false;
  b2cToken: string | null = null;

  stateData: Array<{ State: string; Code?: string }> = [];
  countryList: Array<{ value: string; label: string }> = [];

  initialTin: string | null = null;
  rawInvoicePayload: any = null;

  isSubmitting = false;

  buyerTinLoading = false;
  buyerTinValid = false;
  buyerTinDetails: any = null;
  buyerTinError = '';
  isAutoLookup = false;

  todayString = new Date().toISOString().slice(0, 10);
  minDateString = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();

  submissionId: string | null = null;
  claimStatus: 'PROCESSING' | 'VALID' | 'INVALID' | 'ERRORED' | null = null;
  claimErrorMessage = '';
  claimPdfUrl: string | null = null;
  checkingStatus = false;
  currentYear = new Date().getFullYear();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private svc: B2cClaimService,
    private toastr: ToastrService,
    private router: Router,
  ) {
    this.setThreeStepMetadata();
    this.readBrandMeta();
  }

  ngOnInit(): void {
    const url = new URL(window.location.href);
    const params: Record<string, string> = {};
    url.searchParams.forEach((v, k) => (params[k] = v));

    this.initialTin = params['tin'] || params['supplierTin'] || params['sellerBusinessID'] || null;

    const supplierTin = params['supplierTin'] || params['sellerBusinessID'];
    const docNo = params['docNo'] || params['docNumber'];
    const docDate = params['docDate'];
    const totalInvoiceValue = params['totalInvoiceValue'] || params['totalValue'];

    const patch: Record<string, string> = {};
    if (supplierTin) patch.supplierTin = supplierTin;
    if (docNo) patch.docNumber = docNo;
    if (docDate) patch.docDate = docDate;
    if (totalInvoiceValue) patch.totalValue = totalInvoiceValue;
    if (Object.keys(patch).length) this.invoiceForm.patchValue(patch);

    if (supplierTin && docNo && docDate && totalInvoiceValue) {
      if (!this.isDateWithinLast30Days(docDate)) {
        this.toastr.error('Invoice date must be within the last 30 days.');

        // ✅ SIMPLE FIX: clear date field
        this.invoiceForm.get('docDate')?.setValue('');

        this.skipSearch = false;
        this.lookupSuccess = false;
        this.currentStep = 1;
      } else {
        this.lookupInvoiceAuto({ supplierTin, docNo, docDate, totalInvoiceValue });
      }
    } else {
      this.skipSearch = false;
      this.lookupSuccess = false;
      this.setThreeStepMetadata();
      this.currentStep = 1;
    }

    this.invoiceForm.valueChanges.subscribe(() => {
      if (this.skipSearch && this.isInvoiceDetailsStep(1)) this.fillPreview();
    });

    this.populateCountryList();
    this.populateStateList();

    this.buyerForm.get('idType')?.valueChanges.subscribe(() => {
      const idCtrl = this.buyerForm.get('idNumber');
      idCtrl?.markAsTouched(); // 🔑 KEY LINE
      this.setIdValidator();
    });
    this.setIdValidator();

    this.invoiceForm.get('docDate')?.valueChanges.subscribe(() => {
      const ctrl = this.invoiceForm.get('docDate');
      if (!ctrl || !ctrl.touched) return;

      if (ctrl.errors?.['dateWithin30']) {
        this.toastr.warning('Document date must be within the last 30 days.');
      } else if (ctrl.errors?.['required']) {
        this.toastr.warning('Document date is required.');
      }
    });

    this.buyerForm.get('buyerTin')?.valueChanges.subscribe((value) => {
      const currentTin = (value || '').toString().trim();

      // If TIN changes after validation → force re-verify
      if (this.buyerTinValid && currentTin !== this.lastVerifiedTin) {
        this.buyerTinValid = false;
        this.lastVerifiedTin = null;
        this.buyerTinError = 'Buyer TIN changed. Please verify again.';
      }

      // If field cleared → reset everything
      if (!currentTin) {
        this.buyerTinValid = false;
        this.lastVerifiedTin = null;
        this.buyerTinError = '';
      }
    });
  }

  private readBrandMeta() {
    const protocol = this.document.location.protocol;
    const hostname = this.document.location.host;

    this.logoUrl = `${protocol}//${hostname}/logos/sovos.png`;
    this.brandMeta = {
      logo: `${protocol}//${hostname}/logos/sovos.png`,
      logoInverted: `${protocol}//${hostname}/logos/sovos.inverted.png`,
      favicon: `${protocol}//${hostname}/favicon.webp`,
      brandName: 'Sovos',
      shortName: 'Sovos',
      theme: 'default',
    };
    try {
      const el = this.document.getElementById('brandFavicon') as HTMLLinkElement | null;
      if (el && this.brandMeta?.favicon) el.setAttribute('href', this.brandMeta.favicon);
    } catch {
      // noop
    }
  }

  private setTwoStepMetadata() {
    this.steps = [
      { id: 1, title: 'Invoice Details', subtitle: 'Preview invoice' },
      { id: 2, title: 'Buyer', subtitle: 'Your information' },
    ];
    this.captions = {
      1: 'Step 1 of 2 – Invoice details',
      2: 'Step 2 of 2 – Provide buyer details',
    };
    this.stickyCaptions = {
      1: 'Step 1 of 2 – Invoice details',
      2: 'Step 2 of 2 – Buyer details',
    };
    this.progress = {
      1: '50%',
      2: '100%',
    };
  }

  private readonly GENERAL_TINS = ['EI00000000010', 'EI00000000030'];

  private setThreeStepMetadata() {
    this.steps = [
      { id: 1, title: 'Search', subtitle: 'Search invoice details' },
      { id: 2, title: 'Invoice Details', subtitle: 'Invoice details' },
      { id: 3, title: 'Buyer', subtitle: 'Your information' },
    ];
    this.captions = {
      1: 'Step 1 of 3 – Search invoice details',
      2: 'Step 2 of 3 – Invoice details',
      3: 'Step 3 of 3 – Provide buyer details',
    };
    this.stickyCaptions = {
      1: 'Step 1 of 3 – Confirm invoice',
      2: 'Step 2 of 3 – Invoice details',
      3: 'Step 3 of 3 – Buyer details',
    };
    this.progress = {
      1: '33%',
      2: '66%',
      3: '100%',
    };
  }

  private lookupInvoiceAuto(args: {
    supplierTin?: string;
    docNo?: string;
    docDate?: string;
    totalInvoiceValue?: string | number;
  }) {
    this.lookupInProgress = true;
    this.lookupSuccess = false;
    this.isAutoLookup = true;

    this.svc
      .fetchInvoiceByQr(args)
      .pipe(finalize(() => (this.lookupInProgress = false)))
      .subscribe(
        (resp) => {
          if (resp?.status && resp.response) {
            const payload: any = resp.response;
            this.b2cInvoiceId = payload?.id || null;
            console.log('b2c ID ', this.b2cInvoiceId);
            this.rawInvoicePayload = payload;
            this.applyInvoicePayload(payload, args);
            this.toastr.success('Invoice found and loaded.');
          } else {
            this.lookupSuccess = false;
            this.skipSearch = false;
            this.setThreeStepMetadata();
            this.currentStep = 1;
            this.toastr.warning(
              resp?.message ?? 'Invoice not found automatically. Please try Lookup.',
            );
          }
        },
        (err) => {
          this.lookupInProgress = false;
          this.lookupSuccess = false;
          this.skipSearch = false;
          this.setThreeStepMetadata();
          this.toastr.error('Lookup failed (network). Please try again.');
          console.error('lookupInvoiceAuto error', err);
        },
      );
  }

  lookupInvoice(): void {
    // mark touched so template validation messages display when user clicks Lookup
    this.invoiceForm.markAllAsTouched();

    const supplierTin = this.invoiceForm.get('supplierTin')?.value || undefined;
    const docNo = this.invoiceForm.get('docNumber')?.value || undefined;
    const docDate = this.invoiceForm.get('docDate')?.value || undefined;
    const totalInvoiceValue = this.invoiceForm.get('totalValue')?.value || undefined;

    if (!supplierTin && !docNo && !docDate && !totalInvoiceValue) {
      this.toastr.warning('Please fill at least one field to lookup invoice.');
      return;
    }

    if (this.invoiceForm.invalid) {
      this.toastr.warning('Please fix validation errors before lookup.');
      return;
    }

    this.lookupInProgress = true;
    this.lookupSuccess = false;

    this.svc
      .fetchInvoiceByQr({
        supplierTin,
        docNo,
        docDate,
        totalInvoiceValue,
      })
      .pipe(finalize(() => (this.lookupInProgress = false)))
      .subscribe(
        (resp) => {
          if (resp?.status && resp.response) {
            const payload: any = resp.response;
            this.b2cInvoiceId = payload?.id || null;
            this.rawInvoicePayload = payload;
            this.applyInvoicePayload(payload, { supplierTin, docNo, docDate, totalInvoiceValue });
            this.toastr.success('Invoice found and loaded.');
          } else {
            this.lookupSuccess = false;
            this.skipSearch = false;
            this.setThreeStepMetadata();
            this.toastr.warning(resp?.message ?? 'Invoice not found. Check details and try again.');
          }
        },
        (err) => {
          this.lookupInProgress = false;
          this.lookupSuccess = false;
          this.skipSearch = false;
          this.setThreeStepMetadata();
          this.toastr.error('Lookup failed (network). Please try again.');
          console.error('lookupInvoice error', err);
        },
      );
  }

  /**
   * Apply API payload to form + review object.
   * Accepts the server response object (the `response` node you pasted).
   */
  private applyInvoicePayload(
    payload: any,
    fallbackArgs?: {
      supplierTin?: string;
      docNo?: string;
      docDate?: string;
      totalInvoiceValue?: string | number;
    },
  ) {
    this.lookupSuccess = true;

    if (this.isAutoLookup) {
      // QR / URL based lookup → 2-step flow
      this.skipSearch = true;
      this.setTwoStepMetadata();
      this.currentStep = 1; // Invoice Details
    } else {
      // Manual search → keep 3-step flow
      this.skipSearch = false;
      this.setThreeStepMetadata();
      this.currentStep = 2; // Invoice Details ✅
    }

    const docDate =
      this.invoiceForm.get('docDate')?.value ||
      fallbackArgs?.docDate ||
      payload.transaction?.billPeriodStart ||
      payload.issuedate ||
      payload.createdDate ||
      '';

    const totalValue =
      payload.totalPayableAmount ||
      payload.invoiceValues?.totalInvoiceIncludingTaxAmount ||
      payload.invoiceValues?.totalPayableAmount ||
      fallbackArgs?.totalInvoiceValue ||
      '';

    this.invoiceForm.patchValue({
      supplierTin:
        payload.tin ||
        payload.supplier?.supplierTin ||
        fallbackArgs?.supplierTin ||
        this.invoiceForm.value.supplierTin,
      docNumber:
        payload.docNumber ||
        payload.transaction?.docNumber ||
        fallbackArgs?.docNo ||
        this.invoiceForm.value.docNumber,
      docDate: docDate ? this.normalizeDateForInput(docDate) : this.invoiceForm.value.docDate,
      totalValue: String(totalValue),
    });

    this.fillPreviewFromInvoicePayload(payload);

    // token logic unchanged
    const tinForToken =
      payload?.tin ||
      payload?.supplier?.supplierTin ||
      fallbackArgs?.supplierTin ||
      this.initialTin ||
      null;

    if (tinForToken) {
      this.svc.getToken(tinForToken).subscribe(
        (tokenResp) => {
          let tokenStr: string | null = null;
          try {
            if (typeof tokenResp === 'string') tokenStr = tokenResp;
            else if (tokenResp?.response) tokenStr = tokenResp.response;
          } catch {
            tokenStr = null;
          }
          this.b2cToken = tokenStr;
        },
        () => (this.b2cToken = null),
      );
    }

    this.isAutoLookup = false;
  }

  // Normalize ISO-ish dates to yyyy-mm-dd for date input when possible
  private normalizeDateForInput(d: string): string {
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      const yyyy = dt.getFullYear();
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return d;
    }
  }

  // Keep existing fillPreview (uses invoiceForm + buyerForm) for when we don't have server payload
  fillPreview() {
    const invoice: any = this.invoiceForm.value;
    const buyer: any = this.buyerForm.value;
    const amount = Number(invoice.totalValue) || 0;
    const tax = +(amount * 0.06).toFixed(2);
    const net = +(amount - tax).toFixed(2);

    const buyerAddress = [buyer.addr1, buyer.addr2, buyer.addr3].filter(Boolean).join(', ');
    const invoiceAddressLines = (invoice as any).addressLines || null;
    const addressLines = buyerAddress || invoiceAddressLines || '—';

    const buyerLocationArr = [buyer.city, buyer.state, buyer.country].filter(Boolean);
    const buyerLocation = buyerLocationArr.length
      ? `${buyer.city || '—'} / ${buyer.state || '—'} / ${buyer.country || '—'}`
      : null;
    const invoiceLocationArr = [
      (invoice as any).city,
      (invoice as any).state,
      (invoice as any).country,
    ].filter(Boolean);
    const invoiceLocation = invoiceLocationArr.length
      ? `${(invoice as any).city || '—'} / ${(invoice as any).state || '—'} / ${(invoice as any).country || '—'}`
      : null;

    this.review = {
      // flattened human friendly strings
      amount: `RM ${amount.toFixed(2)}`,
      tax: `RM ${tax.toFixed(2)}`,
      net: `RM ${net.toFixed(2)}`,
      docNumber: invoice.docNumber || '—',
      docDate: invoice.docDate || '—',
      supplierTin: invoice.supplierTin || '—',
      buyerName: buyer.name || '—',
      buyerId: `${buyer.idType || 'IC'} • ${buyer.idNumber || '—'}`,
      buyerContact: buyer.contact || '—',
      buyerEmail: buyer.email || '—',
      buyerTin: buyer.buyerTin || '—',
      buyerSst: buyer.buyerSst || '—',
      addressLines,
      location: buyerLocation || invoiceLocation || '—',
      postal: buyer.postal || (invoice as any).postal || '—',
      items: (invoice as any).items || [],
      sellerName: (invoice as any).sellerName || '—',
      // keep raw nested placeholders empty
      supplier: null,
      shipTo: null,
      invoiceValues: null,
      taxTypeGroupings: null,
      itemDetails: null,
      tin: invoice.supplierTin || null,
    };
  }

  // Map server payload into review object that template expects
  private fillPreviewFromInvoicePayload(p: any) {
    const supplier = p.supplier || {};
    const shipTo = p.shipTo || {};
    const invoiceValues = p.invoiceValues || {};
    const transaction = p.transaction || {};

    // ---------- Amounts ----------
    const totalPayable = invoiceValues.totalPayableAmount ?? p.totalPayableAmount ?? null;

    const totalInclTax = invoiceValues.totalInvoiceIncludingTaxAmount ?? null;
    const totalExclTax = invoiceValues.totalInvoiceExcludingTaxAmount ?? null;
    const taxAmount = invoiceValues.invoiceTotalTaxAmount ?? null;

    // ---------- Review object used by template ----------
    this.review = {
      /* ================= TOP TILES ================= */
      amount: totalPayable != null ? `RM ${Number(totalPayable).toFixed(2)}` : null,
      docNumber: p.docNumber || transaction.docNumber || '—',
      docDate:
        this.invoiceForm.get('docDate')?.value ||
        transaction.billPeriodStart ||
        p.issuedate ||
        p.createdDate ||
        '—',

      /* ================= TRANSACTION ================= */
      supplierTin: p.tin || supplier.supplierTin || '—',

      document: {
        currencyCode: p.document?.currencyCode || 'MYR',
        documentType: p.document?.documentType || 'Invoice',
      },

      /* ================= SUPPLIER ================= */
      supplier: {
        supplierName: supplier.supplierName,
        supplierTin: supplier.supplierTin,
        supplierSST: supplier.supplierSST,
        supplierEmail: supplier.supplierEmail,
        supplierContactNumber: supplier.supplierContactNumber,
        supplierAddLine0: supplier.supplierAddLine0,
        supplierAddLine1: supplier.supplierAddLine1,
        supplierAddLine2: supplier.supplierAddLine2,
        supplierCity: supplier.supplierCity,
        supplierState: supplier.supplierState,
        supplierCountry: supplier.supplierCountry,
        supplierPostalZone: supplier.supplierPostalZone,
      },

      /* ================= BUYER / SHIP TO ================= */
      shipTo: {
        name: shipTo.shipToName,
        tin: shipTo.shipToTIN,
        idType: shipTo.shipToIDType,
        id: shipTo.shipToID,
        address: [
          shipTo.shipToAddLine0,
          shipTo.shipToAddLine1,
          shipTo.shipToAddLine2,
          shipTo.shipToCity,
          shipTo.shipToState,
          shipTo.shipToCountry,
          shipTo.shipToPostalZone,
        ]
          .filter(Boolean)
          .join(', '),
      },

      /* ================= ITEMS ================= */
      items: p.itemDetails || [],

      /* ================= TAX ================= */
      invoiceValues: {
        totalInvoiceExcludingTaxAmount: totalExclTax,
        invoiceTotalTaxAmount: taxAmount,
        totalInvoiceIncludingTaxAmount: totalInclTax,
        totalPayableAmount: totalPayable,
      },

      taxTypeGroupings: p.taxTypeGroupings || [],

      /* ================= META ================= */
      tin: p.tin,
      _rawPayload: p, // keep original (useful for updateEinvoice)
    };
  }

  // Helper used from template to format supplier/shipTo address into a single comma separated string
  formatAddress(obj: any): string | null {
    if (!obj) return null;
    const parts: string[] = [];

    if (obj.supplierAddLine0 || obj.supplierAddLine1 || obj.supplierAddLine2) {
      if (obj.supplierAddLine0) parts.push(String(obj.supplierAddLine0).trim());
      if (obj.supplierAddLine1) parts.push(String(obj.supplierAddLine1).trim());
      if (obj.supplierAddLine2) parts.push(String(obj.supplierAddLine2).trim());
      const city = obj.supplierCity || obj.city;
      const state = obj.supplierState || obj.state;
      const country = obj.supplierCountry || obj.country;
      const postal = obj.supplierPostalZone || obj.supplierPostal || obj.postal || obj.postalZone;
      if (city) parts.push(String(city).trim());
      if (state) parts.push(String(state).trim());
      if (country) parts.push(String(country).trim());
      if (postal) parts.push(String(postal).trim());
    } else if (obj.shipToAddLine0 || obj.shipToAddLine1 || obj.shipToAddLine2) {
      if (obj.shipToAddLine0) parts.push(String(obj.shipToAddLine0).trim());
      if (obj.shipToAddLine1) parts.push(String(obj.shipToAddLine1).trim());
      if (obj.shipToAddLine2) parts.push(String(obj.shipToAddLine2).trim());
      const city = obj.shipToCity || obj.city;
      const state = obj.shipToState || obj.state;
      const country = obj.shipToCountry || obj.country;
      const postal = obj.shipToPostalZone || obj.postal || obj.postalZone;
      if (city) parts.push(String(city).trim());
      if (state) parts.push(String(state).trim());
      if (country) parts.push(String(country).trim());
      if (postal) parts.push(String(postal).trim());
    } else {
      const candidateProps = [
        'addressLines',
        'address',
        'line1',
        'line2',
        'line3',
        'addLine0',
        'addLine1',
        'addLine2',
      ];
      for (const k of candidateProps) {
        if (obj[k]) parts.push(String(obj[k]).trim());
      }
      if (obj.city) parts.push(String(obj.city).trim());
      if (obj.state) parts.push(String(obj.state).trim());
      if (obj.country) parts.push(String(obj.country).trim());
      if (obj.postal) parts.push(String(obj.postal).trim());
    }

    const cleaned = parts.filter(Boolean).map((s) => s.replace(/\s+/g, ' ').trim());
    if (!cleaned.length) return null;
    return cleaned.join(', ');
  }

  /**
   * Set validators for buyer idNumber based on idType.
   * Called on idType change and once on init.
   */
  setIdValidator(): void {
    const idType = this.buyerForm.get('idType')?.value;
    const idControl = this.buyerForm.get('idNumber');

    if (!idControl) return;

    idControl.clearValidators();

    if (idType === 'IC' || idType === 'ARMY') {
      idControl.setValidators([Validators.required, Validators.pattern(/^\d{12}$/)]);
    }

    if (idType === 'PASSPORT') {
      idControl.setValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(20),
      ]);
    }

    // 🚨 DO NOT suppress events
    idControl.updateValueAndValidity();
  }

  /**
   * Build countryList from canonical countries constant.
   */
  private populateCountryList(): void {
    try {
      if (Array.isArray(countries) && countries.length) {
        this.countryList = countries.map((c: any) => ({
          value: c.Code ?? c.code ?? c.alpha2 ?? c.alpha3 ?? c.Country ?? c.name,
          label: c.Country ?? c.name ?? c.label ?? c.CountryName ?? String(c.Code),
        }));
      } else {
        this.countryList = [
          { value: 'MY', label: 'Malaysia' },
          { value: 'SG', label: 'Singapore' },
          { value: 'IN', label: 'India' },
          { value: 'OTHER', label: 'Other' },
        ];
      }
    } catch {
      this.countryList = [
        { value: 'MY', label: 'Malaysia' },
        { value: 'SG', label: 'Singapore' },
        { value: 'IN', label: 'India' },
      ];
    }
  }

  /**
   * Build stateData from canonical stateList constant.
   */
  private populateStateList(): void {
    try {
      if (Array.isArray(stateList) && stateList.length) {
        this.stateData = stateList.map((s: any) => ({
          State: s.State ?? s.name ?? s.state,
          Code: s.Code ?? s.code,
        }));
      } else {
        this.stateData = [{ State: 'Selangor' }, { State: 'Kuala Lumpur' }, { State: 'Penang' }];
      }
    } catch {
      this.stateData = [{ State: 'Selangor' }, { State: 'Kuala Lumpur' }, { State: 'Penang' }];
    }
  }

  /**
   * getSSTValue: same behaviour as your EinvoiceRecipient helper
   */
  getSSTValue(value: any): string {
    const serviceTaxValue = value?.serviceTaxNumber;
    const salesTaxValue = value?.salesTaxNumber;
    if (serviceTaxValue === 'NA' && salesTaxValue === 'NA') return 'NA';
    if (serviceTaxValue === 'NA') return salesTaxValue || '';
    if (salesTaxValue === 'NA') return serviceTaxValue || '';
    return serviceTaxValue && salesTaxValue
      ? `${serviceTaxValue};${salesTaxValue}`
      : serviceTaxValue || salesTaxValue || '';
  }

  /**
   * submitClaim - full flow:
   * 1) addBuyer (existing)
   * 2) generateEinvoice (uses invoice details)
   * 3) on generate response -> get draftId
   * 4) call einvoiceService.updateEinvoice(draftId, payload, '', false, token)
   *    where payload is raw invoice payload + buyer details merged + url values
   * 5) show success screen
   */
  async submitClaim(): Promise<void> {
    if (!this.buyerForm.get('name')?.value) {
      this.toastr.error('Buyer name is required.');
      return;
    }

    // build addBuyer payload (same as before)
    const addBuyerPayload = {
      buyerName: this.buyerForm.value.name,
      buyerIdType: this.buyerForm.value.idType,
      buyerId: this.buyerForm.value.idNumber,
      buyerTin: this.buyerForm.value.buyerTin,
      buyerEmail: this.buyerForm.value.email,
      buyerSST: this.buyerForm.value.buyerSst,
      buyerAddLine0: this.buyerForm.value.addr1,
      buyerAddLine1: this.buyerForm.value.addr2,
      buyerAddLine2: this.buyerForm.value.addr3,
      buyerPostalZone: this.buyerForm.value.postal,
      buyerCity: this.buyerForm.value.city,
      buyerState: this.buyerForm.value.state,
      buyerCountry: this.getCountryLabel(this.buyerForm.value.country!),
      buyerContact: this.buyerForm.value.contact,
      sellerBusinessID: this.invoiceForm.value.supplierTin,
      docNo: this.invoiceForm.value.docNumber,
      docDate: this.invoiceForm.value.docDate,
      totalPayableAmount: Number(this.invoiceForm.value.totalValue) || 0,
      supplierTin: this.invoiceForm.value.supplierTin,
    };

    // mark submitting UI
    this.isSubmitting = true;

    try {
      // Step 1: add buyer
      const resp = await this.svc.addBuyer(addBuyerPayload).toPromise();

      if (!resp?.status) {
        this.toastr.error(resp?.message || 'Failed to submit claim');
        this.isSubmitting = false;
        return;
      }

      // (optional) keep a flag / message that buyer add succeeded
      this.toastr.success('Buyer saved — proceeding to generate eInvoice.');

      // Step 2: prepare generate body from rawInvoicePayload + fallback to review/invoiceForm
      const review = this.review || {};
      const raw = this.rawInvoicePayload || review._rawPayload || null;

      const tinToUse =
        this.initialTin ||
        (raw && (raw.tin || raw.supplier?.supplierTin)) ||
        review?.tin ||
        (this.invoiceForm.value && this.invoiceForm.value.supplierTin) ||
        addBuyerPayload.supplierTin ||
        null;

      const genBody: any = {
        currencyCode:
          (raw?.document?.currencyCode ||
            raw?.invoiceValues?.currencyCode ||
            raw?.invoiceValues?.currency) ??
          review?.invoiceValues?.currencyCode ??
          'MYR',
        currencyExchangeRate: raw?.document?.currencyExchangeRate ?? '',
        docType: raw?.document?.documentType || raw?.document?.internalDocumentType || 'Invoice',
        invType: raw?.document?.invoiceType || 'B2B',
        supplyType: raw?.document?.supplyType || 'Sales',
        tin: tinToUse,
        tranactionType:
          raw?.document?.transactionType || raw?.transaction?.transactionType || 'REGULAR',
        // include doc ids
        docNumber: this.invoiceForm.value.docNumber || raw?.docNumber || undefined,
        docDate: this.invoiceForm.value.docDate || raw?.transaction?.documentDt || undefined,
        totalPayableAmount:
          raw?.invoiceValues?.totalPayableAmount ??
          raw?.totalPayableAmount ??
          (Number(this.invoiceForm.value.totalValue) || undefined),
        items: raw?.itemDetails ?? raw?.items ?? review?.itemDetails ?? undefined,
        invoiceType: raw?.document?.invoiceType ?? raw?.eInvoiceType ?? undefined,
        b2cInvoiceId: this.b2cInvoiceId,
      };

      // clean undefined
      Object.keys(genBody).forEach((k) => {
        if (genBody[k] === undefined) delete genBody[k];
      });

      if (!genBody.tin) {
        this.toastr.warning('Cannot generate eInvoice: missing TIN.');
        this.isSubmitting = false;
        return;
      }

      // Step 3: call generateEinvoice
      this.svc.generateEinvoice(genBody).subscribe(
        (gResp) => {
          console.log('generateEinvoice response', gResp);

          if (!gResp?.status || !gResp?.response?.id) {
            this.toastr.error(gResp?.message || 'Failed to create draft eInvoice.');
            this.isSubmitting = false;
            return;
          }

          const draftId = gResp.response.id;
          this.generatedID = gResp.response.id;
          // build update payload: start from raw payload when possible (so we preserve server shape)
          const updatePayload: any = raw ? JSON.parse(JSON.stringify(raw)) : {};

          // Merge buyer form values into update payload's billTo/buyer fields (or top-level keys if shape differs)
          updatePayload.submitted = true;
          updatePayload.status = null;

          // put buyer details into billTo if present in raw, otherwise use billTo root keys
          updatePayload.billTo = updatePayload.billTo ?? {};
          updatePayload.billTo.buyerName = this.buyerForm.value.name;
          if (this.buyerForm.value.buyerTin)
            updatePayload.billTo.buyerTin = this.buyerForm.value.buyerTin;
          updatePayload.billTo.buyerIDType = this.buyerForm.value.idType;
          updatePayload.billTo.buyerID =
            this.buyerForm.value.idNumber || updatePayload.billTo.buyerID;
          updatePayload.billTo.buyerSST =
            this.buyerForm.value.buyerSst || updatePayload.billTo.buyerSST;
          updatePayload.billTo.buyerAddLine0 =
            this.buyerForm.value.addr1 || updatePayload.billTo.buyerAddLine0;
          updatePayload.billTo.buyerAddLine1 =
            this.buyerForm.value.addr2 || updatePayload.billTo.buyerAddLine1;
          updatePayload.billTo.buyerAddLine2 =
            this.buyerForm.value.addr3 || updatePayload.billTo.buyerAddLine2;
          updatePayload.billTo.buyerPostalZone =
            this.buyerForm.value.postal || updatePayload.billTo.buyerPostalZone;
          updatePayload.billTo.buyerCity =
            this.buyerForm.value.city || updatePayload.billTo.buyerCity;
          updatePayload.billTo.buyerCountry =
            this.getCountryLabel(this.buyerForm.value.country!) ||
            updatePayload.billTo.buyerCountry;

          updatePayload.billTo.buyerState =
            this.buyerForm.value.state || updatePayload.billTo.buyerState;
          updatePayload.billTo.buyerEmail =
            this.buyerForm.value.email || updatePayload.billTo.buyerEmail;
          updatePayload.billTo.buyerContactNumber =
            this.buyerForm.value.contact || updatePayload.billTo.buyerContactNumber;

          // Ensure top-level doc identifiers reflect invoiceForm/url if available
          updatePayload.tin = updatePayload.tin || tinToUse || this.invoiceForm.value.supplierTin;
          updatePayload.docNumber = updatePayload.docNumber || this.invoiceForm.value.docNumber;
          updatePayload.transaction = updatePayload.transaction || {};
          updatePayload.transaction.docNumber =
            updatePayload.transaction.docNumber || this.invoiceForm.value.docNumber;
          updatePayload.transaction.documentDt =
            updatePayload.transaction.documentDt || this.invoiceForm.value.docDate;

          // Merge supplier info from raw kept as is (we prefer raw)
          // If you want to override supplier fields from url, do it here (we don't override)

          // Step 4: call updateEinvoice — PASS STORED TOKEN (this.b2cToken) so header Authorization is set
          this.svc.updateEinvoice(draftId, updatePayload, '', false, this.b2cToken).subscribe(
            (uResp) => {
              this.isSubmitting = false;

              // ❌ FAILURE PATH
              if (!uResp || uResp.status !== 1) {
                this.submitted = true;
                this.claimStatus = 'ERRORED';

                this.claimErrorMessage =
                  uResp?.message ||
                  uResp?.fieldErrors?.[0]?.message ||
                  'Submission failed. Please try again.';

                this.toastr.error(this.claimErrorMessage);
                return;
              }

              // ✅ SUCCESS PATH
              this.submissionId = draftId;
              this.submissionUID = uResp.response?.[0]?.submissionUID || '';

              this.submitted = true;
              this.claimStatus = 'PROCESSING';

              this.toastr.success(
                'Your invoice request has been submitted to LHDN for eInvoice generation.',
              );
            },
            (uErr) => {
              console.error('updateEinvoice error', uErr);
              this.isSubmitting = false;
              this.toastr.error('Network error while submitting eInvoice.');
            },
          );
        },
        (gErr) => {
          console.error('generateEinvoice error', gErr);
          this.isSubmitting = false;
          this.toastr.error('Failed to create draft eInvoice.');
        },
      );
    } catch (err) {
      console.error('submitClaim error', err);
      this.isSubmitting = false;
      this.toastr.error('Network error submitting claim.');
    }
  }

  toggleAdvanced() {
    this.advancedOpen = !this.advancedOpen;
  }

  onStepKeyup(evt: Event, step: StepId) {
    const ke = evt as KeyboardEvent;
    if (ke.key === 'Enter' || ke.key === ' ') {
      ke.preventDefault();
      this.goToStep(step);
    }
  }

  onAdvancedKeyup(evt: Event) {
    const ke = evt as KeyboardEvent;
    if (ke.key === 'Enter' || ke.key === ' ') {
      ke.preventDefault();
      this.toggleAdvanced();
    }
  }

  // navigation helpers
  goToStep(step: StepId | number) {
    const numeric = Number(step);
    const maxAllowed = Math.min(this.maxStepId(), (this.currentStep as number) + 1);

    if (numeric <= maxAllowed && numeric >= 1) {
      this.currentStep = numeric as StepId;

      // 🔐 DO NOT overwrite server-loaded preview
      if (this.isInvoiceDetailsStep(this.currentStep)) {
        if (!this.rawInvoicePayload) {
          this.fillPreview(); // only fallback preview
        }
      }
    }
  }

  goBack() {
    if (this.skipSearch) {
      if (this.currentStep === 2) this.goToStep(1);
    } else {
      const prev = Math.max(1, (this.currentStep as number) - 1);
      this.goToStep(prev);
    }
  }

  mobileNext() {
    if (this.isSubmitting) return; // prevent double submit from sticky footer

    if (this.skipSearch) {
      if (this.currentStep === 1) {
        this.goToStep(2);
        return;
      }
      if (this.currentStep === 2) {
        this.submitClaim();
        return;
      }
    } else {
      if (this.currentStep === 1) {
        if (!this.lookupSuccess) {
          this.toastr.info('Please Lookup invoice first.');
          return;
        }
        this.goToStep(2);
        return;
      }
      if (this.currentStep === 2) {
        this.goToStep(3);
        return;
      }
      if (this.currentStep === 3) {
        this.submitClaim();
        return;
      }
    }
  }

  private maxStepId(): number {
    return this.skipSearch ? 2 : 3;
  }

  isInvoiceDetailsStep(step: StepId): boolean {
    return this.skipSearch ? step === 1 : step === 2;
  }

  /**
   * Whether a step should be enabled (used to disable step buttons).
   */
  isStepEnabled(step: number) {
    const max = this.maxStepId();
    if (step > max) return false;

    if (step === 1) return true;

    if (step === 2) {
      // invoice details step
      return this.skipSearch || this.lookupSuccess;
    }

    if (step === 3) {
      const nameValid = !!this.buyerForm.get('name')?.value && this.buyerForm.get('name')?.valid;
      const contactValid =
        !!this.buyerForm.get('contact')?.value && this.buyerForm.get('contact')?.valid;
      return nameValid && contactValid;
    }

    return false;
  }

  // ---------- NEW: Buyer TIN validation helpers ----------
  /**
   * Returns true when we can fire a validate request.
   */
  canValidateTin(): boolean {
    const tin = (this.buyerForm.get('buyerTin')?.value || '').toString().trim();
    return !!tin && !this.buyerTinLoading;
  }

  /**
   * Calls the B2C service to validate a Buyer TIN using the real svc.verifyTin method.
   * - If called with force=true it will try even when form control hasn't been touched.
   * - Uses this.svc.verifyTin({ idType, idValue, tin }, this.b2cToken)
   */
  validateBuyerTin(force = false): void {
    const tinRaw = this.buyerForm.get('buyerTin')?.value;
    const tin = (tinRaw || '').toString().trim();

    // Clear previous state
    this.buyerTinError = '';
    this.buyerTinDetails = null;
    this.buyerTinValid = false;

    if (!tin) {
      this.buyerTinError = 'Please enter Buyer TIN to validate.';
      return;
    }

    if (!force && !this.canValidateTin()) {
      // if not forced and cannot validate (already loading), return early
      return;
    }

    // Use idType/idNumber from form
    const idType = this.buyerForm.get('idType')?.value || '';
    const idValue = this.buyerForm.get('idNumber')?.value || '';

    this.buyerTinLoading = true;

    // Call the real service verifyTin(VerifyTin, token) method:
    this.svc
      .verifyTin({ idType: idType, idValue: idValue, tin }, this.b2cToken)
      .pipe(finalize(() => (this.buyerTinLoading = false)))
      .subscribe(
        (valid: boolean) => {
          if (valid) {
            this.buyerTinValid = true;
            this.buyerTinError = '';
            this.lastVerifiedTin = tin; // 🔒 lock verified TIN

            // verifyTin returns boolean; if you have a separate detail endpoint you can
            // call it here to fetch buyer details and populate buyerTinDetails.
            this.toastr.success('Buyer TIN validated.');
          } else {
            this.buyerTinValid = false;
            this.buyerTinError = 'Buyer TIN validation failed. Please check and try again.';
          }
        },
        (err: any) => {
          console.error('validateBuyerTin error', err);
          this.buyerTinValid = false;
          this.buyerTinError = 'Network error validating TIN. Try again.';
        },
      );
  }

  getCountryLabel(code: string): string {
    return this.countryList.find((c) => c.value === code)?.label ?? code;
  }

  isStickyDisabled(): boolean {
    if (this.isSubmitting) return true;

    // STEP 1: Search
    if (!this.skipSearch && this.currentStep === 1) {
      return this.invoiceForm.invalid || this.lookupInProgress;
    }

    // STEP 2: Invoice preview (never block)
    if (
      (this.skipSearch && this.currentStep === 1) ||
      (!this.skipSearch && this.currentStep === 2)
    ) {
      return false;
    }

    // STEP 3: Buyer details
    if (this.currentStep === (this.skipSearch ? 2 : 3)) {
      const f = this.buyerForm;

      // ✅ ONLY required fields
      const requiredInvalid =
        f.get('name')?.invalid ||
        f.get('idNumber')?.invalid ||
        f.get('contact')?.invalid ||
        f.get('addr1')?.invalid ||
        f.get('city')?.invalid ||
        f.get('state')?.invalid ||
        f.get('country')?.invalid;

      const tinVerificationMissing = this.isTinVerificationRequired() && !this.buyerTinValid;

      return !!requiredInvalid || tinVerificationMissing;
    }

    return false;
  }

  dateWithinLast30Days(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const selected = new Date(control.value);
    if (isNaN(selected.getTime())) return null;

    const today = new Date();
    const minDate = new Date();
    minDate.setDate(today.getDate() - 30);

    // normalize time
    selected.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);

    if (selected < minDate || selected > today) {
      return { dateWithin30: true };
    }

    return null;
  }

  newClaim() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/b2c']));

    const win = window.open(url, '_blank');

    if (!win) {
      this.router.navigate(['/b2c']);
    }
  }

  isGeneralTin(tin?: string | null): boolean {
    if (!tin) return false;
    return this.GENERAL_TINS.includes(tin.trim().toUpperCase());
  }

  shouldShowVerifyTin(): boolean {
    const tin = this.buyerForm.get('buyerTin')?.value?.toString().trim();
    if (!tin) return false; // empty → hide
    if (this.isGeneralTin(tin)) return false; // general TIN → hide
    return true; // otherwise show
  }

  isTinVerificationRequired(): boolean {
    const tin = this.buyerForm.get('buyerTin')?.value?.toString().trim();
    if (!tin) return false; // empty → not required
    if (this.isGeneralTin(tin)) return false; // general → not required
    return true; // others → required
  }

  checkStatus(): void {
    this.checkingStatus = true;
    this.claimErrorMessage = '';

    this.svc
      .checkClaim({
        submissionId: this.submissionUID,
        invoiceId: this.generatedID,
        tin: this.review.tin,
      })
      .pipe(finalize(() => (this.checkingStatus = false)))
      .subscribe((resp) => {
        if (!resp?.status || !resp.response) {
          this.claimStatus = 'ERRORED';
          this.claimErrorMessage = resp?.message || 'Unable to fetch submission status.';
          return;
        }

        const { overallStatus, documentSummary, errorMessage } = resp.response;

        // 🔥 NORMALIZE BACKEND STATUS
        const normalizedStatus = (overallStatus || '').toUpperCase();

        switch (normalizedStatus) {
          case 'VALID':
            this.claimStatus = 'VALID';
            this.toastr.success('Your e-invoice has been successfully generated by LHDN');
            break;

          case 'INVALID':
            this.claimStatus = 'INVALID';
            this.claimErrorMessage =
              documentSummary?.[0]?.status === 'Invalid'
                ? 'Your e-invoice could not be generated by LHDN. Please try again later'
                : errorMessage || 'Invoice rejected by LHDN';
            break;

          case 'ERRORED':
            this.claimStatus = 'ERRORED';
            this.claimErrorMessage =
              errorMessage ||
              'Your e-invoice could not be generated by LHDN. Please try again later';
            break;

          case 'PROCESSING':
          default:
            this.claimStatus = 'PROCESSING';
            break;
        }
      });
  }

  printPdf(): void {
    if (!this.submissionUID || !this.review?.tin || !this.generatedID) {
      this.toastr.error('Unable to print PDF. Missing data.');
      return;
    }

    const payload = {
      fieldCriteriaList: [
        {
          field: 'uploadedId',
          operator: 'exists',
          value: true,
        },
        {
          field: 'id',
          operator: 'in',
          value: [this.generatedID],
        },
      ],
      sortCriteria: {
        field: 'createdDate',
        direction: 'DESC' as 'DESC' | 'ASC',
      },
      pageNumber: 0,
      pageSize: 25,
    };

    this.svc
      .printEinvoicePdf(
        payload,
        {
          tin: this.review.tin,
          type: 'sales',
          merge: false,
        },
        this.b2cToken!,
      )
      .subscribe((resp) => {
        if (resp?.status && resp.response) {
          window.open(resp.response, '_blank');
        } else {
          this.toastr.error(resp?.message || 'Failed to generate PDF');
        }
      });
  }

  isDateWithinLast30Days(dateStr: string): boolean {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;

    const today = new Date();
    const minDate = new Date();
    minDate.setDate(today.getDate() - 30);

    // normalize
    d.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    minDate.setHours(0, 0, 0, 0);

    return d >= minDate && d <= today;
  }
}
